import { useEffect, useState } from 'react'
import { Image, RefreshCcw, Save } from 'lucide-react'
import { apiClient } from '../services/api'
import { useAuthStore } from '../services/authStore'
import { useTranslation } from '../contexts/TranslationContext'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { ui } from '../styles/ui'

const DEFAULT_MAX_DIMENSION = 1280
const DEFAULT_MAX_PAYLOAD_BYTES = 900000

function toPositiveInt(value, fallback) {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback
}

function findSetting(settings, keys, fallback) {
  for (const key of keys) {
    const setting = settings.find((item) => item.key === key)
    if (setting?.value != null && String(setting.value).trim() !== '') {
      return toPositiveInt(setting.value, fallback)
    }
  }
  return fallback
}

export default function ReceiptImageSettings() {
  const { user } = useAuthStore()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [maxDimension, setMaxDimension] = useState(String(DEFAULT_MAX_DIMENSION))
  const [maxPayloadBytes, setMaxPayloadBytes] = useState(String(DEFAULT_MAX_PAYLOAD_BYTES))

  const loadSettings = async () => {
    setLoading(true)
    setMessage('')
    try {
      const response = await apiClient.get('/llm-settings')
      const settings = response.data.settings || []
      setMaxDimension(String(findSetting(settings, ['ocr_image_max_width', 'ocr_image_max_dimension'], DEFAULT_MAX_DIMENSION)))
      setMaxPayloadBytes(String(findSetting(settings, ['ocr_image_max_payload_bytes'], DEFAULT_MAX_PAYLOAD_BYTES)))
    } catch (error) {
      setMaxDimension(String(DEFAULT_MAX_DIMENSION))
      setMaxPayloadBytes(String(DEFAULT_MAX_PAYLOAD_BYTES))
      setMessage(error?.message || t('Failed to load receipt image limits', 'Failed to load receipt image limits'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSettings()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setMessage('')
    try {
      const nextMaxDimension = toPositiveInt(maxDimension, DEFAULT_MAX_DIMENSION)
      const nextMaxPayloadBytes = toPositiveInt(maxPayloadBytes, DEFAULT_MAX_PAYLOAD_BYTES)
      await apiClient.put('/llm-settings', {
        settings: [
          {
            key: 'ocr_image_max_width',
            value: nextMaxDimension,
            type: 'number',
            description: 'Maximum longest edge for receipt images before OCR',
          },
          {
            key: 'ocr_image_max_payload_bytes',
            value: nextMaxPayloadBytes,
            type: 'number',
            description: 'Maximum compressed receipt image payload size in bytes',
          },
        ],
      })
      setMessage(t('Receipt image limits saved successfully!', 'Receipt image limits saved successfully!'))
    } catch (error) {
      setMessage(error?.message || t('Failed to save receipt image limits', 'Failed to save receipt image limits'))
    } finally {
      setSaving(false)
    }
  }

  if (!user?.is_admin) {
    return (
      <PageShell>
        <Card>
          <div className={ui.cardBody}>
            <h3 className={ui.title}>{t('Access Denied', 'Access Denied')}</h3>
            <p className={`${ui.subtitle} mt-2`}>
              {t('You are not authorised to view receipt image limits.', 'You are not authorised to view receipt image limits.')}
            </p>
          </div>
        </Card>
      </PageShell>
    )
  }

  if (loading) {
    return (
      <PageShell>
        <LoadingState />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <PageHeader
        title={t('Receipt image limits', 'Receipt image limits')}
        subtitle={t(
          'Shared resize and compression limits used by web, Flutter, and Telegram clients before OCR upload.',
          'Shared resize and compression limits used by web, Flutter, and Telegram clients before OCR upload.',
        )}
        actions={
          <>
            <Button variant="secondary" onClick={loadSettings} disabled={saving}>
              <RefreshCcw className="h-4 w-4" />
              {t('Refresh', 'Refresh')}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4" />
              {saving ? t('Saving...', 'Saving...') : t('Save limits', 'Save limits')}
            </Button>
          </>
        }
      />

      {message && (
        <div className={`${ui.alertInfo} ${message.toLowerCase().includes('failed') ? 'border-danger/40 bg-danger/10 text-danger' : 'mb-4'}`}>
          {message}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2 space-y-6">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <Image className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t('Compression budget', 'Compression budget')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t(
                  'These values are published through /api/public/receipt-image-config and consumed by all clients before upload.',
                  'These values are published through /api/public/receipt-image-config and consumed by all clients before upload.',
                )}
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={ui.label} htmlFor="receipt-image-max-dimension">
                {t('Max dimension', 'Max dimension')}
              </label>
              <input
                id="receipt-image-max-dimension"
                type="number"
                min="1"
                step="1"
                value={maxDimension}
                onChange={(event) => setMaxDimension(event.target.value)}
                className={`mt-1 ${ui.input}`}
              />
              <p className={ui.forms.helpText}>
                {t('Longest edge in pixels after client-side resize.', 'Longest edge in pixels after client-side resize.')}
              </p>
            </div>

            <div>
              <label className={ui.label} htmlFor="receipt-image-max-payload-bytes">
                {t('Max payload bytes', 'Max payload bytes')}
              </label>
              <input
                id="receipt-image-max-payload-bytes"
                type="number"
                min="1"
                step="1"
                value={maxPayloadBytes}
                onChange={(event) => setMaxPayloadBytes(event.target.value)}
                className={`mt-1 ${ui.input}`}
              />
              <p className={ui.forms.helpText}>
                {t('Target compressed image size before OCR upload.', 'Target compressed image size before OCR upload.')}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-sm text-foreground">
            <div className="font-medium">{t('Consumers', 'Consumers')}</div>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
              <li>{t('Legacy frontend upload flow', 'Legacy frontend upload flow')}</li>
              <li>{t('Telegram mini app upload flow', 'Telegram mini app upload flow')}</li>
              <li>{t('Flutter upload and recognition flow', 'Flutter upload and recognition flow')}</li>
            </ul>
          </div>
        </Card>

        <Card className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t('Public endpoint', 'Public endpoint')}
            </h3>
            <p className="mt-2 text-sm text-foreground">GET /api/public/receipt-image-config</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {t(
                'Clients fetch this endpoint on startup or before upload so they can stay aligned with the current admin limits.',
                'Clients fetch this endpoint on startup or before upload so they can stay aligned with the current admin limits.',
              )}
            </p>
          </div>

          <div className="rounded-lg border border-outline bg-surface-elevated p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('Current values', 'Current values')}
            </div>
            <dl className="mt-3 space-y-3 text-sm">
              <div>
                <dt className="text-muted-foreground">{t('Max dimension', 'Max dimension')}</dt>
                <dd className="font-medium text-foreground">{toPositiveInt(maxDimension, DEFAULT_MAX_DIMENSION)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('Max payload bytes', 'Max payload bytes')}</dt>
                <dd className="font-medium text-foreground">{toPositiveInt(maxPayloadBytes, DEFAULT_MAX_PAYLOAD_BYTES).toLocaleString()}</dd>
              </div>
            </dl>
          </div>
        </Card>
      </div>
    </PageShell>
  )
}
